import React, { useEffect, useMemo } from "react"

import { Avatar, Badge, Drawer, Flex, Space, Typography } from "antd"
import dayjs from "dayjs"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import ButtonComponent from "../../../../Components/Button"
import Private from "../../../../Components/Private"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import { paymentProcessorMap, serviceMap } from "../../../../Utils/utils"
import { alertStatusColorMap } from "../../../AlertsPage/utils/alertStatusMapping"
import Creators from "../../reducer"
import CreditRequestStatus from "../CreditRequestStatus/CreditRequestStatus"

import "./CreditDetailsDrawer.less"

const { Link, Text } = Typography

const CreditDetailsDrawer = () => {
  const [search, setSearch] = useSearchParams()
  const creditRequestId = search.get("open_credit_details")
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { pathname } = useLocation()

  const { isMobile, theme } = useSelector((state) => state.nav)
  const { creditDetails, creditDetailsFetching } = useSelector((state) => state.credits)

  const open = !!creditRequestId

  useEffect(() => {
    if (creditRequestId) {
      dispatch(Creators.getCreditDetailsRequest(creditRequestId))
    }
  }, [creditRequestId])

  const handleCloseModal = () => {
    navigate(pathname, { replace: true })
    dispatch(Creators.creditsSetProp("creditDetails", null))
  }

  const rows = useMemo(() => {
    if (!creditDetails) return []

    return [
      {
        label: "Alert status",
        key: "alert_status",
        render: (value, details) =>
          value ? (
            <Flex align="center" gap={11}>
              <Badge color={alertStatusColorMap[value] || alertStatusColorMap["default"]} />
              <Text strong>{value}</Text>
            </Flex>
          ) : (
            <Text strong>-</Text>
          )
      },
      {
        label: "Business account",
        key: "business_account",
        render: (ba, details) => (
          <Flex gap={4} align="center">
            <Avatar
              className="user_avatar"
              size={16}
              style={{
                backgroundColor: ba?.photo ? "transparent" : "#225AEA",
                borderRadius: "4px",
                border: "none",
                fontSize: 12,
                marginRight: 4,
                minWidth: 16
              }}
              src={ba?.photo || null}
            >
              {!ba?.photo ? ba?.business_account?.[0] : null}
            </Avatar>
            <Text strong style={{ maxWidth: "100%" }}>
              <Private>{ba?.business_account}</Private>
            </Text>
          </Flex>
        )
      },
      {
        label: "Billing descriptor",
        key: "merchant_descriptor",
        render: (value) =>
          value ? (
            <Text strong>
              <Private>{value}</Private>
            </Text>
          ) : (
            <Text strong>-</Text>
          )
      },
      {
        label: "Alert source",
        key: "alert_service",
        render: (value) => <Text strong>{serviceMap[value] || "-"}</Text>
      },
      {
        label: "Alert ID",
        key: "alert_id",
        render: (value) =>
          value ? (
            <Text strong>
              <Private>{value}</Private>
            </Text>
          ) : (
            <Text strong>-</Text>
          )
      },
      {
        label: "Type",
        key: "alert_type",
        render: (value) => <Text strong>{value?.[0]?.toUpperCase() + value?.slice(1) || "-"}</Text>
      },
      {
        label: "Alert date",
        key: "alert_timestamp",
        render: (value) => (
          <Text strong>{value ? dayjs(value).format("D MMM YYYY h:mm A [(UTC)]") : "-"}</Text>
        )
      },
      {
        label: "Card",
        key: "card_last4",
        render: (value, details) => (
          <Space>
            {details.brand && (
              <img
                src={Images[`${details?.brand?.toLowerCase()}${theme === "dark" ? "_dark" : ""}`]}
                width={20}
                height={20}
                alt={details?.brand}
              />
            )}
            <Text strong>
              {details?.card_last4 ? (
                <>
                  {details?.brand} **** <Private mask="••••">{details.card_last4}</Private>
                </>
              ) : (
                "-"
              )}
            </Text>
          </Space>
        )
      },
      {
        label: "Transaction date",
        key: "transaction_timestamp",
        render: (value) => (
          <Text strong>{value ? dayjs(value).format("D MMM YYYY h:mm A [(UTC)]") : "-"}</Text>
        )
      },
      {
        label: "Payment processor",
        key: "payment_processor",
        render: (value, details) => (
          <Space>
            {value !== "other" && value !== "-" && (
              <img src={Images[`integration_${value}`]} width={20} height={20} alt={value} />
            )}
            <Text strong>{paymentProcessorMap[value] || "-"}</Text>
          </Space>
        )
      },
      {
        label: "Matched transaction ID",
        key: "transaction_id",
        render: (value, details) => {
          if (details?.transaction_id?.[0] && details?.transaction_link?.[0])
            return (
              <Link
                href={details.transaction_link[0]}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                copyable={{ icon: <Icon type="copy" /> }}
                strong
              >
                <Private>{details.transaction_id[0]}</Private>
              </Link>
            )

          if (details?.transaction_id?.[0])
            return (
              <Text strong copyable={{ icon: <Icon type="copy" /> }}>
                <Private>{details?.transaction_id?.[0]}</Private>
              </Text>
            )

          return <Text strong>-</Text>
        }
      },
      {
        label: "Order value",
        key: "amount",
        render: (value, details) =>
          details?.["converted_currency"] ? (
            <Flex vertical gap={4}>
              <Text type="secondary" strong>
                <Private>{details?.amount}</Private>
                {` ${details?.currency || ""}`}
              </Text>
              <Text strong>
                <Private>{details?.["converted_amount"]}</Private>
                {` ${details?.["converted_currency"]}`}
              </Text>
            </Flex>
          ) : (
            <Text strong>
              <Private>{details?.amount}</Private>
              {` ${details?.currency || ""}`}
            </Text>
          )
      },
      {
        label: "ARN",
        key: "arn",
        render: (value) =>
          value ? (
            <Text strong>
              <Private>{value}</Private>
            </Text>
          ) : (
            <Text strong>-</Text>
          )
      },
      {
        label: "Auth code",
        key: "auth_code",
        render: (value) =>
          value ? (
            <Text copyable={{ icon: <Icon type={"copy"} /> }} strong>
              <Private>{value}</Private>
            </Text>
          ) : (
            <Text strong>-</Text>
          )
      },
      {
        label: "Credit request status",
        key: "request_status",
        render: (value, details) => <CreditRequestStatus cr={details} />
      }
    ]
  }, [creditDetails])

  return (
    <Drawer
      title="Credit details"
      getContainer={() => document.getElementById("main-layout")}
      width={600}
      placement="right"
      closable={false}
      onClose={handleCloseModal}
      open={!!open}
      destroyOnClose={true}
      className="credit-details-drawer"
      loading={creditDetailsFetching}
      styles={{
        content: { borderRadius: isMobile ? 0 : "24px" },
        header: { padding: !isMobile ? "16px 24px" : "16px", border: "none" },
        footer: { padding: "8px 24px 24px", border: "none" }
      }}
      rootClassName="drawer-like-modal"
      extra={
        <ButtonComponent
          type="link"
          shape="circle"
          onClick={handleCloseModal}
          icon={<Icon type="cross_small" style={{ color: "#7A7A83" }} />}
        />
      }
      footer={
        <Flex className="contact_support_footer" justify="space-between" align="center" gap={10}>
          <Text strong>Experiencing troubles or need help?</Text>
          <ButtonComponent
            icon={<Icon style={{ color: "#7A7A83" }} type="support_circle" />}
            onClick={() => window?.Intercom("show")}
          >
            Contact support
          </ButtonComponent>
        </Flex>
      }
    >
      <div className="details_grid">
        {rows.map(({ label, key, render }) => (
          <React.Fragment key={key}>
            <Text className="col1" type="secondary" style={{ letterSpacing: "0px" }}>
              {label}
            </Text>
            <div className="col2">
              {render ? (
                render(creditDetails?.[key], creditDetails)
              ) : (
                <Text strong>{creditDetails?.[key] || "-"}</Text>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </Drawer>
  )
}

export default CreditDetailsDrawer
